/* eslint-disable @angular-eslint/prefer-standalone */
import { FavoriteTarget, ModalFavoriteResolver } from '@services/user-favorites/favorite-url.util';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, QueryParamsHandling, Router } from '@angular/router';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { DateFormatterService } from '@core/services/helpers/date-formatter.service';
import { CalendarDateEvent } from '@interfaces/a-organizar/events/calendarDateEvent';
import { CalendarEvent } from '@interfaces/a-organizar/events/calendarEvent';
import { Option } from '@interfaces/common/option';
import { type ChatAutoInstruction } from '@features/dashboard/ai/pages/chat/chat-v2.component';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DashboardService } from '@services/http/dashboard.service';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { CompanySelectionService } from '@services/helpers/company-selection.service';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { AppState } from '@src/app/state';
import { selectCompanyId } from '@src/app/state/auth/auth.selectors';
import { selectPageFilterCompanyIds } from '@src/app/state/global/global.selectors';
import { MenuItem } from 'primeng/api';
import { Subject, combineLatest, take, takeUntil } from 'rxjs';
import { FeatureFlagStore } from '@services/feature-flags/feature-flag.store';
import {
  EARLY_SETTLEMENT_BADGE,
  earlySettlementDetailRows,
  isEarlySettledLoanEvent,
} from './utils/calendar-early-settlement.util';
import { HomeLayoutService } from './services/home-layout.service';
import { HomeBlockId } from './utils/home-blocks';
import {
  HomeBlockConditions,
  availableHomeBlocks,
  homeSelectionHints,
  renderableHomeBlocks,
} from './utils/home-block-availability';
import {
  groupHomeRows,
  homeRowClass as buildHomeRowClass,
  isStretchedHomePair,
} from './utils/group-home-rows';

interface CalendarMonthChangeEvent {
  month?: number;
  year?: number;
}

export interface CalendarDate {
  day: number;
  month: number;
  year: number;
}

interface CalendarPreviewEvent {
  date: string;
  dateObject: Date;
  event: CalendarEvent;
  index: number;
  title: string;
  typeLabel: string;
  currencyCode: string;
  value: number;
  detailRows: CalendarPreviewDetailRow[];
}

interface CalendarPreviewDetailRow {
  label: string;
  value: string;
}

/**
 * Home do Cognito (`/dashboard/dashboard-management`), card 3786. De cima para baixo: barra do Cog,
 * Economia Prevista + Calendário de Vencimentos, Performance da Dívida, Score de Crédito (uma empresa)
 * e os cards Limites Bancários e Reuniões. Os indicadores de mercado (KPIs, curvas DI1 e PTAX,
 * indicadores-chave, calendário econômico) saíram da Home nesta entrega (decisão D-09/P-04).
 * Desde o card 3788 a ordem e quais blocos aparecem vêm da arrumação da pessoa (`HomeLayoutService`, gaveta
 * Personalizar); a barra do Cog fica sempre no topo. Regras, endpoints e permissões: README desta pasta.
 */
@Component({
  selector: 'app-dashboard-management',
  standalone: false,
  templateUrl: './dashboard-management.component.html',
  styleUrls: ['./dashboard-management.component.scss'],
  providers: [HomeLayoutService],
})
export class DashboardManagementComponent extends VaultBaseComponent implements OnInit, OnDestroy {
  @ViewChild(DialogComponent) private calendarEventsDialog: DialogComponent | null = null;
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: 'Cognito',
  };
  // "Home" desde o card 3785 (antes "Dashboard"), igual ao item do menu. É também o título padrão do favorito.
  breadcrumbs: MenuItem[] = [
    {
      routerLink: '/dashboard/dashboard-management',
      label: 'Home',
    },
  ];
  private readonly calendarFavoriteResolver = new ModalFavoriteResolver();

  /** Selo da parcela liquidada antes do vencimento na tabela do calendário (card 3201). */
  readonly earlySettlementBadge = EARLY_SETTLEMENT_BADGE;
  currentDate: Date = new Date();
  calendarEvents: CalendarDateEvent[] = [];
  private destroy$ = new Subject<void>();
  selectedCompanyIds: string[] = [];
  selectedCompanyId: string | null = null;
  // Flag temporária de rollout do cenário de economia. Remover quando a feature
  // ficar 1-2 semanas com enabledForAll=true sem incidentes.
  dashboardEconomyScenarioEnabled = false;
  highlightedDatesSet = new Set<string>();
  private pendingModalParams: { modal: string; date: string } | null = null;
  private isOpeningModalFromClick = false;
  private readonly featureFlagStore = inject(FeatureFlagStore);
  private readonly companySelection = inject(CompanySelectionService);

  /** Permissões de cada bloco novo da Home (card 3786), a mesma que o backend confere. */
  canViewCreditScore = false;
  canViewBankLimits = false;
  canListMeetings = false;
  canViewCompanyMeetings = false;

  /** Muda a cada Atualizar do cabeçalho; os cards de Limites e Reuniões releem ao ver a mudança. */
  refreshKey = 0;

  /** Pergunta da barra do Cog, entregue ao chat do botão flutuante (abre o painel ao chegar). */
  cogInstruction: ChatAutoInstruction | null = null;

  /** Arrumação da Home (card 3788): ordem e blocos ligados, da conta ou do navegador. */
  readonly homeLayout = inject(HomeLayoutService);

  /** Gaveta "Personalizar Dashboard" aberta. */
  customizeOpen = false;

  /** Itens extras do menu de três pontos do breadcrumb (depois de "Adicionar aos Favoritos"). */
  readonly breadcrumbMenuItems: MenuItem[] = [
    {
      label: 'Personalizar',
      icon: 'pi pi-sliders-h',
      state: { hint: 'Ordem e blocos da sua Home' },
      command: () => this.openCustomize(),
    },
  ];

  constructor(
    translateService: TranslateService,
    private dashboardService: DashboardService,
    public dateFormatter: DateFormatterService,
    protected override store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private permissionsService: PermissionsService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    super(translateService);
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * REGRA DE NEGÓCIO: a seção "Cenário de Economia do Cliente" (cards de Economia
   * Prevista + gráfico Performance da Dívida) NÃO aparece com o escopo
   * "Todas empresas" — exige pelo menos uma empresa explicitamente selecionada
   * (`selectedCompanyIds`). Motivo: sem recorte de empresa os valores consolidariam
   * o tenant inteiro, o que não é o objetivo do cenário (leitura por cliente).
   * Também exige a feature flag `dashboard-economy-scenario` habilitada (rollout).
   */
  get shouldShowDashboardEconomyScenario(): boolean {
    return this.dashboardEconomyScenarioEnabled && this.selectedCompanyIds.length > 0;
  }

  /** Score de Crédito só com UMA empresa escolhida e `CREDITSCORE_VIEW_COMPANY` (decisão P-05). */
  get shouldShowCreditScore(): boolean {
    return this.canViewCreditScore && !!this.selectedCompanyId;
  }

  /** Card Reuniões: `MEETING_VIEW_COMPANY` (cliente) ou `MEETING_LIST` (time interno). */
  get shouldShowMeetings(): boolean {
    return this.canViewCompanyMeetings || this.canListMeetings;
  }

  isCompanyIdUser = false;

  /** Sem poder listar empresas, o filtro de empresa do cabeçalho nem chega a ser montado. */
  readonly companyListPermission = Permissions.Company.LIST;

  ngOnInit(): void {
    this.initializePageFilterCompanySelection();
    this.store
      .select(selectCompanyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(companyId => {
        if (companyId) {
          const changed = this.applyDashboardCompanyIds([companyId]);
          this.isCompanyIdUser = true;
          if (changed) {
            this.reloadCompanyScopedBlocks();
          }
        }
      });
    this.loadDashboardEconomyScenarioToggle();
    this.loadBlockPermissions();
    this.homeLayout.init();

    const today = this.currentDate;
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    this.getCalendarEvents(currentMonth, currentYear);

    // Escutar mudanças na URL para controlar o modal
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.checkAndOpenModalFromUrl(params);
    });
  }

  onDashboardManagementCompanySelectedMultiple(selectedOptions: Option[] | null): void {
    this.applyDashboardCompanyIds((selectedOptions ?? []).map(option => String(option.value)));
    this.handleCompanyChange();
  }

  onDashboardManagementCompanySelected(option: Option | null): void {
    this.applyDashboardCompanyIds(option?.value ? [String(option.value)] : []);
    this.handleCompanyChange();
  }

  /** Barra do Cog: cada pergunta vira uma instrução nova (id único) e o chat do Cog abre com ela. */
  onAskCog(question: string): void {
    this.cogInstruction = {
      id: `home-cog-${this.dateFormatter.getNowTimestamp()}`,
      visibleMessage: question,
      agentMessage: question,
    };
    this.cdr.markForCheck();
  }

  /** Abre a gaveta "Personalizar Dashboard" (menu de três pontos do breadcrumb ou estado vazio). */
  openCustomize(): void {
    this.customizeOpen = true;
    this.cdr.markForCheck();
  }

  /** Flag, permissões e empresa escolhida: decidem que blocos existem e quais renderizam agora. */
  private get blockConditions(): HomeBlockConditions {
    return {
      economyScenarioEnabled: this.dashboardEconomyScenarioEnabled,
      hasCompanySelection: this.selectedCompanyIds.length > 0,
      hasSingleCompany: !!this.selectedCompanyId,
      canViewCreditScore: this.canViewCreditScore,
      canViewBankLimits: this.canViewBankLimits,
      canViewMeetings: this.shouldShowMeetings,
    };
  }

  /**
   * Linhas da Home na arrumação da pessoa (card 3788): só blocos ligados que renderizam agora, agrupados por largura.
   * Vazio até a arrumação ser lida, para os blocos não montarem duas vezes (padrão e depois a da pessoa).
   */
  get homeRows(): HomeBlockId[][] {
    if (!this.homeLayout.loaded()) return [];
    return groupHomeRows(renderableHomeBlocks(this.homeLayout.visibleIds(), this.blockConditions));
  }

  /**
   * Classe da linha (regra pura em `utils/group-home-rows.ts`): bloco sozinho ocupa a linha; Economia Prevista ao lado
   * de outro bloco mantém a grade 5/8 do protótipo; só Limites + Reuniões e Economia + Calendário esticam.
   * @param row Blocos da linha.
   */
  homeRowClass(row: HomeBlockId[]): string {
    return buildHomeRowClass(row);
  }

  /**
   * Os cards de economia dividem a altura do bloco ao lado só quando o par estica (Economia + Calendário, Task 3839);
   * sozinhos ou ao lado de outro bloco ficam com a altura natural.
   * @param row Blocos da linha.
   */
  economyFillsRow(row: HomeBlockId[]): boolean {
    return isStretchedHomePair(row);
  }

  /**
   * Home sem nenhum bloco na tela (só a barra do Cog): `all-off` quando a pessoa desligou tudo; `needs-company` quando
   * há blocos ligados, mas todos dependem de uma empresa escolhida (crítica G1).
   */
  get homeEmptyState(): 'all-off' | 'needs-company' | null {
    if (!this.homeLayout.loaded()) return null;
    if (this.homeLayout.allHidden()) return 'all-off';
    return this.homeRows.length === 0 ? 'needs-company' : null;
  }

  /** Avisa ao serviço de arrumação quais blocos existem para a pessoa e quais a empresa de agora não mostra. */
  private syncAvailableBlocks(): void {
    const conditions = this.blockConditions;
    this.homeLayout.setAvailable(availableHomeBlocks(conditions));
    this.homeLayout.setSelectionHints(homeSelectionHints(conditions));
  }

  /** Atualizar do cabeçalho: relê o calendário e os cards de Limites e Reuniões. */
  refreshDashboardData(): void {
    const currentMonth = this.currentDate.getMonth();
    const currentYear = this.currentDate.getFullYear();
    this.fetchCalendarEventsForTwoMonths(currentMonth, currentYear);
    this.refreshKey++;
    this.cdr.markForCheck();
  }

  private loadBlockPermissions(): void {
    combineLatest([
      this.permissionsService.hasPermission(Permissions.CreditScore.VIEW_COMPANY),
      this.permissionsService.hasPermission(Permissions.BankRelationship.LIST),
      this.permissionsService.hasPermission(Permissions.Meeting.LIST),
      this.permissionsService.hasPermission(Permissions.Meeting.VIEW_COMPANY),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([creditScore, bankLimits, meetingList, meetingViewCompany]) => {
        this.canViewCreditScore = creditScore;
        this.canViewBankLimits = bankLimits;
        this.canListMeetings = meetingList;
        this.canViewCompanyMeetings = meetingViewCompany;
        this.syncAvailableBlocks();
        this.cdr.markForCheck();
      });
  }

  private initializePageFilterCompanySelection(): void {
    // Prioridade (card 3571): URL explicita (`companyId=all` ou CSV) > estado global. Com `all` antes de a lista
    // de empresas ser conhecida, comeca sem filtro (todas) e o app-select-company emite a selecao completa ao carregar.
    const params = this.route.snapshot.queryParams;
    const queryCompanyIds = this.companySelection.resolveUrlCompanyIds(params);

    if (queryCompanyIds.length > 0 || this.companySelection.isAllRequested(params)) {
      this.applyDashboardCompanyIds(queryCompanyIds);
      return;
    }

    this.store
      .select(selectPageFilterCompanyIds)
      .pipe(take(1))
      .subscribe(companyIds => {
        const changed = this.applyDashboardCompanyIds(companyIds);
        if (changed) {
          this.reloadCompanyScopedBlocks();
        }
      });
  }

  private applyDashboardCompanyIds(companyIds: string[] | null | undefined): boolean {
    const normalizedCompanyIds = this.normalizeCompanyIds(companyIds);
    const nextCompanyId = normalizedCompanyIds.length === 1 ? normalizedCompanyIds[0] : null;
    const changed =
      this.selectedCompanyId !== nextCompanyId ||
      this.selectedCompanyIds.join('|') !== normalizedCompanyIds.join('|');

    this.selectedCompanyIds = normalizedCompanyIds;
    this.selectedCompanyId = nextCompanyId;
    this.syncAvailableBlocks();
    this.cdr.markForCheck();
    return changed;
  }

  private normalizeCompanyIds(companyIds: string[] | null | undefined): string[] {
    return [...new Set((companyIds ?? []).map(id => String(id).trim()).filter(Boolean))].sort();
  }

  /**
   * Disponibilidade do cenário de economia para o usuário atual, mantida em dia pelo FeatureFlagStore
   * (reconsulta ao voltar para a aba e ao navegar; liberou, aparece sem relogar, card 3323).
   * O backend protege o endpoint real; esta consulta só evita renderizar a UI para quem não está no rollout.
   */
  private loadDashboardEconomyScenarioToggle(): void {
    this.featureFlagStore
      .watch$('dashboard-economy-scenario')
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        this.dashboardEconomyScenarioEnabled = enabled;
        this.syncAvailableBlocks();
        this.cdr.markForCheck();
      });
  }

  handleCompanyChange(): void {
    this.reloadCompanyScopedBlocks();
  }

  /** Troca de empresa: o calendário relê aqui; os cards novos releem pelo próprio `companyId`. */
  private reloadCompanyScopedBlocks(): void {
    if (!this.isBrowser) return;
    this.calendarEvents = [];
    this.updateHighlightedDatesSet();

    const currentMonth = this.currentDate.getMonth();
    const currentYear = this.currentDate.getFullYear();
    this.fetchCalendarEventsForTwoMonths(currentMonth, currentYear);
  }

  // ─────────────────────────── Calendário de Vencimentos ───────────────────────────

  getCalendarEvents(month: number, year: number) {
    this.fetchCalendarEventsForTwoMonths(month, year);
  }

  onMonthChange(event: CalendarMonthChangeEvent): void {
    if (event.month === undefined || event.year === undefined) {
      return;
    }
    const month = event.month - 1;
    const year = event.year;
    this.fetchCalendarEventsForTwoMonths(month, year);
  }

  /**
   * Carrega os vencimentos dos dois meses exibidos no calendario.
   *
   * Sempre repassa `selectedCompanyIds`: o backend so recorta por empresa quando recebe o
   * filtro, entao omitir a selecao faz o calendario listar vencimento de outras empresas do
   * escopo do usuario. Selecao vazia = "Todas empresas" (consolidado), que e o comportamento
   * esperado do placeholder do seletor.
   */
  fetchCalendarEventsForTwoMonths(month: number, year: number): void {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 2, 0);
    const firstDayString = this.formatDate(firstDay);
    const lastDayString = this.formatDate(lastDay);

    this.dashboardService
      .getDashboardCalendar(firstDayString, lastDayString, this.selectedCompanyIds)
      .subscribe({
        next: data => {
          this.calendarEvents = data || [];
          this.updateHighlightedDatesSet();
          this.checkPendingModalAfterDataLoad();
          this.cdr.markForCheck();
        },
        error: (error: { message?: string } | null) => {
          console.warn('Calendar API error:', error?.message || error);
          this.calendarEvents = [];
          this.updateHighlightedDatesSet();
          this.cdr.markForCheck();
        },
      });
  }

  formatDate(date: CalendarDate | Date): string {
    const year = (date as CalendarDate).year || (date as Date).getFullYear();
    const month = (
      '0' +
      ((date as CalendarDate).month >= 0
        ? (date as CalendarDate).month
        : (date as Date).getMonth() + 1)
    ).slice(-2);
    const day = ('0' + ((date as CalendarDate).day || (date as Date).getDate())).slice(-2);
    return `${year}-${month}-${day}`;
  }

  private updateHighlightedDatesSet() {
    try {
      const dates = this.calendarEvents
        .filter((d: CalendarDateEvent) => d.events && d.events.length > 0)
        .map((event: CalendarDateEvent) => event.date);
      this.highlightedDatesSet = new Set(dates);
    } catch (error) {
      console.warn('Error updating highlighted dates set:', error);
      this.highlightedDatesSet = new Set();
    }
  }

  isHighlighted(date: CalendarDate): boolean {
    // Proteção contra erros durante change detection
    if (!date || !this.highlightedDatesSet) return false;

    try {
      const dateString = this.formatDate({ ...date, month: date.month + 1 });
      return this.highlightedDatesSet.has(dateString);
    } catch (error) {
      return false;
    }
  }

  isSelected(date: CalendarDate): boolean {
    // Retorne false imediatamente se não há data selecionada
    if (!this.selectedDate || !date) return false;

    try {
      const dateString = this.formatDate({ ...date, month: date.month + 1 });
      const selectedString = this.formatDate(this.selectedDate);
      return dateString === selectedString;
    } catch (error) {
      return false;
    }
  }

  selectedDate?: Date;

  selectedDateEvents: CalendarEvent[] = [];
  tablePage = 0;

  private previousMonth: number = -1;
  private previousYear: number = -1;

  get calendarEventsCount(): number {
    return this.calendarEvents.reduce((count, item) => count + (item.events?.length ?? 0), 0);
  }

  get upcomingCalendarEvents(): CalendarPreviewEvent[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return this.calendarEvents
      .flatMap((item, dateIndex) => {
        const dateObject = new Date(`${item.date}T12:00:00`);
        return (item.events ?? []).map((event, eventIndex) =>
          this.buildCalendarPreviewEvent(item.date, dateObject, event, dateIndex * 100 + eventIndex)
        );
      })
      .filter(item => item.dateObject >= today)
      .sort((a, b) => a.dateObject.getTime() - b.dateObject.getTime())
      .slice(0, 4);
  }

  private buildCalendarPreviewEvent(
    date: string,
    dateObject: Date,
    event: CalendarEvent,
    index: number
  ): CalendarPreviewEvent {
    const title = this.getCalendarEventTitle(event);
    const typeLabel = this.getCalendarEventTypeLabel(event);
    const currencyCode = this.getCalendarEventCurrencyCode(event);
    const value = this.getCalendarEventValue(event);

    return {
      date,
      dateObject,
      event,
      index,
      title,
      typeLabel,
      currencyCode,
      value,
      detailRows: this.getCalendarEventDetailRows(event, dateObject, currencyCode, value),
    };
  }

  getCalendarEventTitle(event: CalendarEvent): string {
    if (event.loanOperation) {
      return this.translateReference(event.loanOperation.reference) || 'Empréstimo';
    }

    if (event.comexOperation) {
      return this.translateReference(event.comexOperation.reference) || 'Comex';
    }

    if (event.derivativeOperation) {
      return this.translateReference(event.derivativeOperation.demandReference) || 'Derivativo';
    }

    return 'Vencimento';
  }

  private getCalendarEventTypeLabel(event: CalendarEvent): string {
    if (event.loanOperation) return 'Empréstimo';
    if (event.comexOperation) return 'Comex';
    if (event.derivativeOperation) return 'Derivativo';

    return event.type || 'Vencimento';
  }

  getCalendarEventCurrencyCode(event: CalendarEvent): string {
    return (
      event.loanOperation?.currencyCode ||
      event.comexOperation?.currencyCode ||
      event.derivativeOperation?.currencyCode ||
      'BRL'
    );
  }

  getCalendarEventValue(event: CalendarEvent): number {
    return (
      event.loanOperation?.value ||
      event.comexOperation?.value ||
      event.derivativeOperation?.notionalAmount ||
      0
    );
  }

  private getCalendarEventDetailRows(
    event: CalendarEvent,
    dateObject: Date,
    currencyCode: string,
    value: number
  ): CalendarPreviewDetailRow[] {
    const earlySettled = isEarlySettledLoanEvent(event);
    const rows: CalendarPreviewDetailRow[] = [
      { label: 'Tipo', value: this.getCalendarEventTypeLabel(event) },
      // Card 3201: parcela liquidada antes do vencimento cai na data da liquidação.
      {
        label: earlySettled ? 'Liquidado em' : 'Vencimento',
        value: this.formatDateLabelFromDate(dateObject),
      },
      ...earlySettlementDetailRows(event, date => this.dateFormatter.formatDateInstant(date, true)),
    ];
    const operation = event.loanOperation || event.comexOperation || event.derivativeOperation;

    this.addCalendarDetailRow(rows, 'Empresa', operation?.companyName);
    this.addCalendarDetailRow(
      rows,
      'Status',
      this.translateCalendarOperationStatus(operation?.operationStatus)
    );
    this.addCalendarDetailRow(rows, 'ID', operation?.sequentialId);

    if (event.loanOperation || event.comexOperation) {
      const installmentOperation = event.loanOperation || event.comexOperation;

      this.addCalendarDetailRow(rows, 'Contrato', installmentOperation.contractId);
      this.addCalendarDetailRow(
        rows,
        'Parcela',
        installmentOperation.installmentNumber
          ? String(installmentOperation.installmentNumber)
          : null
      );
      this.addCalendarDetailRow(
        rows,
        'Valor parcela',
        this.formatCalendarCurrencyValue(currencyCode, installmentOperation.installmentValue)
      );
    }

    this.addCalendarDetailRow(
      rows,
      'Valor total',
      this.formatCalendarCurrencyValue(currencyCode, value)
    );

    return rows;
  }

  private addCalendarDetailRow(
    rows: CalendarPreviewDetailRow[],
    label: string,
    value: string | null | undefined
  ): void {
    if (!value) return;

    rows.push({ label, value });
  }

  private formatCalendarCurrencyValue(
    currencyCode: string,
    value: number | null | undefined
  ): string | null {
    if (value === null || value === undefined || !Number.isFinite(value)) return null;

    return `${currencyCode} ${this.formatLocalDecimal(value, 2)}`;
  }

  private formatDateLabelFromDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  private translateCalendarOperationStatus(status: string | null | undefined): string | null {
    if (!status) return null;

    const normalizedStatus = status.trim();
    const statusLabels: Record<string, string> = {
      Hired: 'Contratado',
      Quoted: 'Cotado',
      Simulated: 'Simulado',
      Draft: 'Rascunho',
      Active: 'Ativo',
      Open: 'Aberto',
      Closed: 'Fechado',
      Settled: 'Liquidado',
      Canceled: 'Cancelado',
      Cancelled: 'Cancelado',
      Expired: 'Expirado',
      Pending: 'Pendente',
    };

    return statusLabels[normalizedStatus] ?? normalizedStatus;
  }

  onSelectDate(selectedDate: Date | null) {
    if (!selectedDate) return;

    this.selectedDate = selectedDate;

    // Check if month changed and update calendar events
    const month = selectedDate.getMonth();
    const year = selectedDate.getFullYear();
    const currentMonth = this.currentDate.getMonth();
    const currentYear = this.currentDate.getFullYear();

    if (month !== currentMonth || year !== currentYear) {
      this.currentDate = selectedDate;
      this.fetchCalendarEventsForTwoMonths(month, year);
    }

    // Detect month change when navigating through calendar
    if (this.previousMonth !== -1 && this.previousYear !== -1) {
      if (month !== this.previousMonth || year !== this.previousYear) {
        this.fetchCalendarEventsForTwoMonths(month, year);
      }
    }
    this.previousMonth = month;
    this.previousYear = year;

    this.selectedDateEvents = this.calendarEvents
      .filter((d: CalendarDateEvent) =>
        this.dateFormatter.datesMatchIgnoringTime(d.date, selectedDate)
      )
      .flatMap((x: CalendarDateEvent) =>
        x.events.map((event: CalendarEvent) => ({
          ...event,
          type: event.type === 'derivative' ? this.t(this.tk.General.Derivative) : event.type,
        }))
      );
    this.tablePage = 0;

    this.openCalendarEventsModalWithUrl(selectedDate);
  }

  /**
   * Favorito da modal do dia (card 3785): só com `?modal=calendar-events&duedate=` na URL, que
   * `checkAndOpenModalFromUrl` lê para reabrir a modal.
   */
  get calendarFavorite(): FavoriteTarget | null {
    const dueDate = this.selectedDate
      ? this.dateFormatter.formatDateToYYYYMMDD(this.selectedDate)
      : '';
    return this.calendarFavoriteResolver.resolve(
      this.router.url,
      { modal: 'calendar-events', duedate: dueDate },
      this.selectedDate
        ? `Vencimentos de ${this.dateFormatter.formatDateInstant(this.selectedDate)}`
        : ''
    );
  }

  private openCalendarEventsModalWithUrl(selectedDate: Date): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    queryParams['modal'] = 'calendar-events';
    queryParams['duedate'] = this.dateFormatter.formatDateToYYYYMMDD(selectedDate);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'replace' as QueryParamsHandling,
    });

    this.calendarEventsDialog
      ?.confirm(
        '',
        this.t(this.tk.Management.NdfQuotationDateHourConfirmation),
        '',
        '',
        '',
        'date-events',
        false,
        false
      )
      .then(() => {
        this.removeModalFromUrl();
      })
      .catch(() => {
        this.removeModalFromUrl();
      });
  }

  private removeModalFromUrl(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams['modal'];
    delete queryParams['duedate'];

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'replace' as QueryParamsHandling,
    });
  }

  private checkAndOpenModalFromUrl(params: Record<string, string | null>): void {
    // Se está abrindo por clique direto, ignorar mudança de URL
    if (this.isOpeningModalFromClick) {
      return;
    }

    if (params['modal'] === 'calendar-events' && params['duedate']) {
      // Criar a data usando o formato ISO para garantir parsing correto
      const dateString = params['duedate'];
      if (dateString) {
        // Adicionar hora para evitar problemas de timezone: YYYY-MM-DD -> YYYY-MM-DDTHH:mm:ss
        const selectedDate = new Date(dateString + 'T12:00:00');

        if (!isNaN(selectedDate.getTime())) {
          // Se os eventos do calendário já foram carregados, abrir o modal imediatamente
          if (this.calendarEvents && this.calendarEvents.length > 0) {
            this.tryOpenModalForDate(selectedDate);
          } else {
            // Se não, guardar os parâmetros para abrir depois que os dados carregarem
            this.pendingModalParams = {
              modal: params['modal'],
              date: params['duedate'],
            };
          }
        }
      }
    } else {
      // Limpar parâmetros pendentes se não há modal na URL
      this.pendingModalParams = null;
    }
  }

  private checkPendingModalAfterDataLoad(): void {
    if (this.pendingModalParams && this.pendingModalParams.modal === 'calendar-events') {
      // Criar a data usando o formato ISO para garantir parsing correto
      const dateString = this.pendingModalParams.date;
      const selectedDate = new Date(dateString + 'T12:00:00');

      if (!isNaN(selectedDate.getTime())) {
        this.tryOpenModalForDate(selectedDate);
      }
      this.pendingModalParams = null;
    }
  }

  private tryOpenModalForDate(selectedDate: Date): void {
    // Converter a data selecionada para string YYYY-MM-DD para comparação
    const selectedDateString = this.dateFormatter.formatDateToYYYYMMDD(selectedDate);

    // Preparar os eventos para a data selecionada
    this.selectedDate = selectedDate;
    this.selectedDateEvents = this.calendarEvents
      .filter((d: CalendarDateEvent) => {
        // Comparar usando strings para evitar problemas de timezone
        const eventDateString = d.date; // já vem como string YYYY-MM-DD
        return eventDateString === selectedDateString;
      })
      .flatMap((x: CalendarDateEvent) =>
        x.events.map((event: CalendarEvent) => ({
          ...event,
          type: event.type === 'derivative' ? this.t(this.tk.General.Derivative) : event.type,
        }))
      );
    this.tablePage = 0;

    this.calendarEventsDialog
      ?.confirm(
        '',
        this.t(this.tk.Management.NdfQuotationDateHourConfirmation),
        '',
        '',
        '',
        'date-events',
        false,
        false
      )
      .then(() => {
        this.removeModalFromUrl();
      })
      .catch(() => {
        this.removeModalFromUrl();
      });
  }

  /**
   * Traduz referências específicas do calendário de vencimentos
   * @param reference Referência original
   * @returns Referência traduzida
   */
  translateReference(reference: string): string {
    if (!reference) return reference;

    // Verificar se contém "WORKING_CAPITAL" (pode vir com texto adicional)
    if (reference.includes('WORKING_CAPITAL')) {
      return reference.replace('WORKING_CAPITAL', 'Capital de Giro');
    }

    return reference;
  }

  private formatLocalDecimal(value: number | null | undefined, fractionDigits = 2): string {
    return (value ?? 0).toLocaleString('pt-BR', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
